import { Link } from 'react-router-dom';
import { services } from '../../mocks/services';
import { locations } from '../../mocks/locations';

const LOGO_URL = 'https://greenworldil.com/wp-content/uploads/2025/06/GW-Logo-100w.png';
const PHONE = '(708) 698-4072';
const PHONE_HREF = 'tel:7086984072';

export default function Footer() {
  const featuredLocations = locations.filter((l) => l.featured);
  const otherLocations = locations.filter((l) => !l.featured).slice(0, 8);

  return (
    <footer className="bg-brand-dark text-white">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Column 1 - Brand */}
        <div className="lg:col-span-1">
          <div className="w-40 h-14 flex items-center mb-5">
            <img
              src={LOGO_URL}
              alt="Green World Construction and Roofing"
              className="h-12 w-auto object-contain brightness-0 invert"
            />
          </div>
          <p className="text-sm text-gray-400 leading-relaxed mb-5">
            Chicago&apos;s trusted roofing and exterior construction company since 1989. Family-owned, licensed &amp; insured. We serve residential and commercial clients throughout Chicagoland.
          </p>
          <div className="flex flex-col gap-3">
            <a href={PHONE_HREF} className="flex items-center gap-2 text-sm text-white hover:text-brand-gold transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-brand-gold text-sm" />
              </div>
              {PHONE}
            </a>
            <a href="mailto:Greenworldroofing@gmail.com" className="flex items-center gap-2 text-sm text-gray-400 hover:text-brand-gold transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line text-brand-gold text-sm" />
              </div>
              Greenworldroofing@gmail.com
            </a>
            <p className="flex items-start gap-2 text-sm text-gray-400">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                <i className="ri-map-pin-2-line text-brand-gold text-sm" />
              </div>
              5801 North Northwest Highway<br />Chicago, Illinois 60631
            </p>
            <p className="flex items-center gap-2 text-sm text-gray-400">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-time-line text-brand-gold text-sm" />
              </div>
              Mon–Fri: 6:30AM–6PM | Sat: 8AM–2PM
            </p>
          </div>
        </div>

        {/* Column 2 - Services */}
        <div>
          <h4 id="footer-services" className="text-sm font-bold text-white uppercase tracking-widest mb-5">
            <a href="/services">Our Services</a>
          </h4>
          <ul className="flex flex-col gap-2.5">
            {services.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/services/${s.slug}`}
                  className="text-sm text-gray-400 hover:text-brand-gold transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-arrow-right-s-line text-brand-gold text-xs" />
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 3 - Locations */}
        <div>
          <h4 id="footer-locations" className="text-sm font-bold text-white uppercase tracking-widest mb-5">
            <a href="/locations">Service Areas</a>
          </h4>
          <ul className="flex flex-col gap-2.5">
            {featuredLocations.map((loc) => (
              <li key={loc.id}>
                <Link
                  to={`/locations/${loc.slug}`}
                  className="text-sm text-gray-400 hover:text-brand-gold transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-arrow-right-s-line text-brand-gold text-xs" />
                  {loc.city} Roofing Services
                </Link>
              </li>
            ))}
            {otherLocations.map((loc) => (
              <li key={loc.id}>
                <Link
                  to={`/locations/${loc.slug}`}
                  className="text-sm text-gray-400 hover:text-brand-gold transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-arrow-right-s-line text-brand-gold text-xs" />
                  {loc.city}, IL
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 4 - Quick Links + CTA */}
        <div>
          <h4 className="text-sm font-bold text-white uppercase tracking-widest mb-5">Quick Links</h4>
          <ul className="flex flex-col gap-2.5 mb-7">
            {[
              { label: 'About Us', href: '/about' },
              { label: 'Contact & Estimate', href: '/contact' },
              { label: 'All Locations', href: '/locations' },
              { label: 'Residential Roofing', href: '/services/shingle-roofing' },
              { label: 'Commercial Roofing', href: '/services/commercial-roofing' },
              { label: 'Tuckpointing', href: '/services/tuckpointing-masonry' },
            ].map((link) => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className="text-sm text-gray-400 hover:text-brand-gold transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-arrow-right-s-line text-brand-gold text-xs" />
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/contact"
            className="block bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-5 py-3 rounded-md text-center transition-colors cursor-pointer whitespace-nowrap"
          >
            Request Free Inspection
          </Link>
        </div>
      </div>

      {/* Trust badges bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex flex-wrap items-center justify-center gap-6 lg:gap-10">
          {['Licensed & Insured', 'Family-Owned Since 1989', 'Residential & Commercial', 'Free Written Estimates', '24+ Communities Served'].map((badge) => (
            <span key={badge} className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap">
              <i className="ri-shield-check-fill text-brand-gold text-xs" />
              {badge}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 bg-brand-dark-2">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Green World Construction and Roofing. All rights reserved.
          </p>
          <p className="text-xs text-gray-500">
            5801 N Northwest Hwy, Chicago, IL 60631 &nbsp;&bull;&nbsp; Serving Chicagoland Since 1989
          </p>
        </div>
      </div>
    </footer>
  );
}
